import { useState } from "react";
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";

import Grid from "@mui/material/Grid";
import FormHelperText from "@mui/material/FormHelperText";
import Typography from "@mui/material/Typography";
import { Button, Form, LoadingOverlay, SubmitButton } from "@covertree/ui";
import FormFileField from "@covertree/ui/forms/FormFileField";
import FormCoverageDocument from "@covertree/ui/forms/FormCoverageDocument";

import { CoverageFilesFormData } from "@forms";
import useAppDispatch from "@redux/hooks/useAppDispatch";
import { Coverage } from "@graphql/api";
import {
  addCurrentExternalCoverageDataToStateForAdditionOfPolicyEvent as prefillForm,
  saveExternalPolicyDocuments,
  setExtractionDocument,
} from "@redux/coverages/coveragesSlice";
import {
  ExtractedExternalCoverage,
  useUploadCoverageExtractionDocumentMutation,
} from "@redux/documents/documentsRestApi";
import {
  BASE_COVERAGE_DATA_PATH,
  COVERAGE_DATA_ROUTES,
} from "@routing/Residents/ProofOfCoverage/CoverageData";
import { moveBackInPathname } from "@utils";

const FOUR_AND_HALF_MB = 4.5 * 1024 * 1024;

/** Shapes extracted data like a Coverage, so the existing coverage-to-form mapper can pre-fill the form. */
const toCoverage = (extraction: ExtractedExternalCoverage) =>
  ({
    agencyAddress: extraction.agencyAddress,
    agencyEmail: extraction.agencyEmail,
    agencyName: extraction.agencyName,
    agencyPhoneNumber: extraction.agencyPhoneNumber,
    annualCost: extraction.annualPremium,
    // Keep Home Details closed when the document had none of them.
    coverageDetails: Object.values(extraction.coverageDetails).some(
      (value) => value !== null
    )
      ? extraction.coverageDetails
      : null,
    insuranceCarrierName: extraction.insuranceCarrierName,
    policyEffectiveDate: extraction.policyEffectiveDate,
    policyEndDate: extraction.policyExpirationDate,
    policyId: extraction.policyNumber ?? "",
  }) as Coverage;

/**
 * Optional first step for an external coverage: upload the policy document and pre-fill the form from it.
 * The upload's id is kept so addExternalCoverage attaches the file; it also shows in the Documents step.
 */
export default function PolicyDocument() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { residentId } = useParams();

  const [error, setError] = useState<"upload" | "extraction" | null>(null);

  const [uploadDocument, { isLoading: isUploading }] =
    useUploadCoverageExtractionDocumentMutation();

  const form = useForm<CoverageFilesFormData>({
    defaultValues: { files: [] },
  });
  const { handleSubmit, setValue, watch } = form;
  const files = watch("files") ?? [];

  const goToPolicyDetails = () => {
    dispatch(saveExternalPolicyDocuments({ files }));
    navigate({
      pathname: `${moveBackInPathname(location.pathname, 1)}/${BASE_COVERAGE_DATA_PATH}/${COVERAGE_DATA_ROUTES.EXTERNAL_COVERAGE}`,
      search: createSearchParams(searchParams).toString(),
    });
  };

  const extractPolicyData = async ({
    files: [file],
  }: CoverageFilesFormData) => {
    setError(null);

    const result = await uploadDocument({ residentId, file })
      .unwrap()
      .catch(() => null);

    if (!result) return setError("upload");

    // The file is stored either way, so addExternalCoverage can attach it even if it could not be read.
    dispatch(setExtractionDocument({ id: result.id, fileName: file.name }));

    if (!result.extraction) return setError("extraction");

    dispatch(prefillForm(toCoverage(result.extraction)));
    goToPolicyDetails();
  };

  if (isUploading) return <LoadingOverlay />;

  return (
    <FormProvider {...form}>
      <Form onSubmit={handleSubmit(extractPolicyData)}>
        <Grid gap={3} display="flex" flexDirection="column">
          <Typography mt={3} fontSize={32}>
            Upload Policy Document
          </Typography>
          <Typography color="text.secondary">
            Upload the declarations page or proof of insurance and we will fill
            in the policy details for you to review. You can also skip this step
            and enter them yourself.
          </Typography>
          {error === "upload" && (
            <FormHelperText error>
              We could not upload this document. Try again, or skip this step
              and enter the policy details manually.
            </FormHelperText>
          )}
          {error === "extraction" && (
            <FormHelperText error>
              We could not read this document. Skip this step and enter the
              policy details manually; the document is saved and will be added
              to the coverage.
            </FormHelperText>
          )}
          <FormFileField
            name="files"
            maxFileSize={FOUR_AND_HALF_MB}
            maxFileAmount={1}
            accept={{
              "image/png": [".png"],
              "image/jpeg": [".jpg", ".jpeg"],
              "application/pdf": [".pdf"],
            }}
          />
          {files.map((file) => (
            <FormCoverageDocument
              key={file.name}
              document={file}
              removeDocument={() => setValue("files", [])}
            />
          ))}
          <Grid container gap={2}>
            <SubmitButton disabled={!files.length} width={300}>
              Fill In From Document
            </SubmitButton>
            <Button variant="outlined" onClick={goToPolicyDetails}>
              Skip and Enter Manually
            </Button>
          </Grid>
        </Grid>
      </Form>
    </FormProvider>
  );
}
